import { memo } from 'react';
import { BlendFunction, ToneMappingMode } from 'postprocessing';

import { EffectComposer } from './EffectComposer.js';
import { FXAA } from './FXAA.js';
import { Outline } from './Outline.js';
import { SMAA } from './SMAA.js';
import { ToneMapping } from './ToneMapping.js';

export interface PostEffectsProps {
  /**
   * The layer to use for selection.
   *
   * @default 10
   */
  selectionLayer?: number;
  /**
   * The color of the outline.
   *
   * @default 'orange'
   */
  selectionOutlineColor?: string;
  /**
   * The strength of the outline.
   *
   * @default 5
   */
  selectionOutlineStrength?: number;
  /**
   * The blend mode of the outline.
   *
   * @default BlendFunction.ADD
   */
  selectionOutlineBlendMode?: BlendFunction;
  /**
   * Enable Antialiasing.
   *
   * @default 'smaa'
   */
  antialias?: boolean | 'smaa' | 'fxaa';
  /**
   * The tone mapping mode.
   *
   * @default ToneMappingMode.OPTIMIZED_CINEON
   */
  toneMapping?: ToneMappingMode;
}

/**
 * Applies a post-processing effects
 * Outlines: the currently selected objects will be outlined.
 * Antiliasing:
 *  - (SMAA) Subpixel Morphological Anti-Aliasing .
 *  - (FXAA) Fast Approximate Anti-Aliasing
 * Tone mapping:
 *  - REINHARD
 *  - REINHARD2
 *  - REINHARD2_ADAPTIVE
 *  - OPTIMIZED_CINEON
 *  - ACES_FILMIC
 *  - UNCHARTED2
 */
export const PostEffects: React.FC<PostEffectsProps> = memo(
  ({
    selectionLayer = 10,
    selectionOutlineColor = 'orange',
    selectionOutlineStrength = 15,
    selectionOutlineBlendMode = BlendFunction.ADD,
    antialias = 'smaa',
    toneMapping = ToneMappingMode.OPTIMIZED_CINEON,
  }) => {
    return (
      <EffectComposer autoClear={false} multisampling={0}>
        <Outline
          blur
          xRay
          blendFunction={selectionOutlineBlendMode}
          edgeStrength={selectionOutlineStrength}
          hiddenEdgeColor={selectionOutlineColor}
          selectionLayer={selectionLayer}
          visibleEdgeColor={selectionOutlineColor}
        />
        {antialias ? antialias || antialias === 'smaa' ? <SMAA /> : <FXAA /> : null}
        <ToneMapping mode={toneMapping} />
      </EffectComposer>
    );
  }
);

export default PostEffects;
